import type { JSX, ReactNode } from 'react'

/**
 * StickyFooter — a sticky footer that keeps form actions reachable while the user
 * scrolls a long form. Give it an `icon` + `eyebrow` + `title` to show a "You are
 * editing / {name}" context strip on the left, so users always know what they're
 * acting on.
 *
 * By default, it shows primary (Save) and secondary (Cancel) buttons. If you only
 * need a close button (when primary actions are in the header), set `closeLabel`
 * and `onClose` to hide the primary/secondary buttons and show only the close button.
 *
 * The primary button can either drive an `onPrimary` callback or submit a form
 * by id (`primaryType="submit"` + `primaryForm`), so it works with both
 * controlled handlers and native `<form>` submission.
 *
 * `extraActions` renders additional controls (e.g. a "Logs" button) to the
 * left of Cancel/Save — pass a `<RecordAuditLogButton .../>` to add a
 * per-record audit log entry point without changing this component's
 * primary/secondary contract.
 */
export function StickyFooter({
  icon = 'pi pi-user',
  eyebrow,
  title,
  children,
  primaryLabel = 'Save',
  primaryIcon,
  onPrimary,
  primaryType = 'button',
  primaryForm,
  secondaryLabel = 'Cancel',
  onSecondary,
  closeLabel,
  onClose,
  loading = false,
  disabled = false,
  className = '',
  extraActions,
}: {
  /** PrimeIcons class for the small context avatar (e.g. `pi pi-user`). */
  icon?: string | undefined
  /** Small muted line above the title, e.g. "You are editing". */
  eyebrow?: string | undefined
  /** Bold context title, e.g. the record's name. */
  title?: string | undefined
  /** Optional extra left-aligned content, used instead of icon/eyebrow/title. */
  children?: ReactNode | undefined
  primaryLabel?: string | undefined
  primaryIcon?: string | undefined
  onPrimary?: (() => void) | undefined
  primaryType?: 'button' | 'submit' | undefined
  primaryForm?: string | undefined
  secondaryLabel?: string | undefined
  onSecondary?: (() => void) | undefined
  closeLabel?: string | undefined
  onClose?: (() => void) | undefined
  loading?: boolean | undefined
  disabled?: boolean | undefined
  className?: string | undefined
  /** Extra controls rendered before Cancel/Save (or before the close button), e.g. a "Logs" button. */
  extraActions?: ReactNode | undefined
}): JSX.Element {
  const showCloseOnly = closeLabel && onClose
  return (
    <div
      className={`sticky bottom-0 z-30 -mx-6 mt-6 border-t border-gray-200 bg-white/90 px-6 py-3.5 backdrop-blur supports-[backdrop-filter]:bg-white/75 ${className}`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          {(eyebrow || title) && (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
              <i className={icon} aria-hidden="true" />
            </span>
          )}
          <div className="min-w-0 text-sm">
            {eyebrow && <p className="text-xs text-gray-500">{eyebrow}</p>}
            {title && <p className="truncate font-semibold text-gray-900">{title}</p>}
            {children}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {extraActions}
          {showCloseOnly ? (
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100"
            >
              <i className="pi pi-times" aria-hidden="true" />
              {closeLabel}
            </button>
          ) : (
            <>
              {onSecondary && (
                <button
                  type="button"
                  onClick={onSecondary}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                >
                  {secondaryLabel}
                </button>
              )}
              <button
                type={primaryType}
                form={primaryForm}
                onClick={onPrimary}
                disabled={loading || disabled}
                className="inline-flex items-center gap-2 rounded-lg bg-[var(--kc3-accent)] px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
              >
                {loading ? (
                  <>
                    <i className="pi pi-spinner pi-spin" aria-hidden="true" />
                    Saving…
                  </>
                ) : (
                  <>
                    {primaryIcon && <i className={primaryIcon} aria-hidden="true" />}
                    {primaryLabel}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
